import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { BillElementOut } from "../../../../../api/bills";
import { refreshBills } from "../../../../../hooks/useBills";
import { nextKitchenHoldDeadlineMs } from "../../../../../lib/kitchenHold";
import { serverNowMs } from "../../../../../lib/serverClock";

/** След `hold_until` — колко да изчака, преди да пита (BE пуска по свой таймер). */
const EXPIRY_GRACE_MS = 1000;
/** BE още я държи (таймерът му е на ~5 s) — пак след толкова. */
const EXPIRY_RETRY_MS = 5000;
/** Таванът на опитите за един срок — после чака обичайните опреснявания. */
const EXPIRY_MAX_REFRESHES = 6;

/**
 * Когато броячът на задържана партида стигне 0, сметките се опресняват
 * веднъж — в Маркиране няма WebSocket за сметките, а без интервал (облак без
 * резерв) другият терминал би гледал „Влиза…" безкрайно. Свежо четене с
 * `refreshBills` (ползва заявката в полет), не invalidate.
 *
 * Срокът е най-ранният `hold_until` на показаната сметка. Докато BE още я
 * държи, свежите данни са същите, срокът не се мени и ефектът не тръгва
 * наново — затова опитите продължават сами на `EXPIRY_RETRY_MS` до тавана.
 * Освободи ли я BE, срокът се сменя (следващата партида или нищо) и старата
 * верига се отменя. Работи и в „Обобщено", където броячи няма.
 */
export const useKitchenHoldExpiryRefresh = (elements: BillElementOut[]) => {
  const queryClient = useQueryClient();
  const deadlineMs = nextKitchenHoldDeadlineMs(elements);

  useEffect(() => {
    if (deadlineMs === null) return;
    let refreshes = 0;
    let timerId: number | undefined;
    const refresh = () => {
      refreshes += 1;
      void refreshBills(queryClient);
      if (refreshes < EXPIRY_MAX_REFRESHES)
        timerId = window.setTimeout(refresh, EXPIRY_RETRY_MS);
    };
    timerId = window.setTimeout(
      refresh,
      Math.max(0, deadlineMs - serverNowMs()) + EXPIRY_GRACE_MS,
    );
    return () => window.clearTimeout(timerId);
  }, [deadlineMs, queryClient]);
};
